import { createPortal } from "react-dom";
import { ChevronRight, Folder, MoreHorizontal, Pencil, Pin, PinOff, Trash2 } from "lucide-react";
import type { Project, Session } from "../../api/types.js";
import { projectDisplayName } from "../shared/constants.js";
import type { FloatingPosition } from "./useFloatingMenu.js";
import { PROJECT_SUBMENU_HEIGHT, PROJECT_SUBMENU_WIDTH, SESSION_MENU_HEIGHT, SESSION_MENU_WIDTH } from "./useFloatingMenu.js";

export function SessionMenu({
  session,
  projects,
  menuOpen,
  menuPosition,
  projectSubmenuOpen,
  projectSubmenuPosition,
  openSessionMenu,
  closeSessionMenu,
  closeProjectMenu,
  openProjectSubmenu,
  closeProjectSubmenu,
  onRenameSession,
  onMoveSession,
  onTogglePinSession,
  onDeleteSession
}: {
  session: Session;
  projects: Project[];
  menuOpen: boolean;
  menuPosition: FloatingPosition | null;
  projectSubmenuOpen: boolean;
  projectSubmenuPosition: FloatingPosition | null;
  openSessionMenu: (sessionId: string, rect: DOMRect, width: number, height: number) => void;
  closeSessionMenu: () => void;
  closeProjectMenu: () => void;
  openProjectSubmenu: (sessionId: string, rect: DOMRect, width: number, height: number) => void;
  closeProjectSubmenu: () => void;
  onRenameSession: (session: Session) => void;
  onMoveSession: (session: Session, projectId: string | null) => void;
  onTogglePinSession: (session: Session) => void;
  onDeleteSession: (session: Session) => void;
}) {
  return (
    <>
      <button
        className="history-menu-button"
        type="button"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-label={`${session.title} 更多操作`}
        onClick={(event) => {
          event.stopPropagation();
          if (menuOpen) {
            closeSessionMenu();
            return;
          }
          closeProjectMenu();
          openSessionMenu(session.id, event.currentTarget.getBoundingClientRect(), SESSION_MENU_WIDTH, SESSION_MENU_HEIGHT);
          closeProjectSubmenu();
        }}
      >
        <MoreHorizontal size={17} />
      </button>
      {menuOpen && menuPosition && createPortal(
        <div
          className="history-menu"
          role="menu"
          data-session-menu-layer
          style={{ top: menuPosition.top, left: menuPosition.left }}
        >
          <button type="button" role="menuitem" onClick={() => { closeSessionMenu(); onRenameSession(session); }}>
            <Pencil size={15} />
            <span>重命名</span>
          </button>
          <div className="history-menu-move-group">
            <button
              className="history-menu-move-toggle"
              type="button"
              role="menuitem"
              aria-haspopup="menu"
              aria-expanded={projectSubmenuOpen}
              aria-controls={`history-project-submenu-${session.id}`}
              onClick={(event) => {
                event.stopPropagation();
                if (projectSubmenuOpen) {
                  closeProjectSubmenu();
                  return;
                }
                openProjectSubmenu(session.id, event.currentTarget.getBoundingClientRect(), PROJECT_SUBMENU_WIDTH, PROJECT_SUBMENU_HEIGHT);
              }}
            >
              <Folder size={15} />
              <span>移动至项目</span>
              <ChevronRight className="history-menu-chevron" size={14} />
            </button>
          </div>
          <button type="button" role="menuitem" onClick={() => { closeSessionMenu(); onTogglePinSession(session); }}>
            {session.pinnedAt ? <PinOff size={15} /> : <Pin size={15} />}
            <span>{session.pinnedAt ? "取消置顶" : "置顶聊天"}</span>
          </button>
          <button className="danger" type="button" role="menuitem" onClick={() => { closeSessionMenu(); onDeleteSession(session); }}>
            <Trash2 size={15} />
            <span>删除</span>
          </button>
        </div>,
        document.body
      )}
      {projectSubmenuOpen && projectSubmenuPosition && createPortal(
        <div
          className="history-project-submenu"
          id={`history-project-submenu-${session.id}`}
          role="menu"
          data-session-menu-layer
          style={{ top: projectSubmenuPosition.top, left: projectSubmenuPosition.left }}
        >
          <div className="history-project-submenu-title">选择项目</div>
          {projects.length === 0 && <div className="history-project-empty">暂无项目</div>}
          {session.projectId && (
            <button
              className="history-project-option history-project-option-out"
              type="button"
              role="menuitem"
              onClick={() => {
                closeSessionMenu();
                onMoveSession(session, null);
              }}
            >
              <span>移出项目</span>
            </button>
          )}
          {projects.map((project) => {
            const isCurrent = project.id === session.projectId;
            return (
              <button
                key={project.id}
                className="history-project-option"
                type="button"
                role="menuitem"
                disabled={isCurrent}
                onClick={() => {
                  if (isCurrent) return;
                  closeSessionMenu();
                  onMoveSession(session, project.id);
                }}
              >
                <span>{projectDisplayName(project)}</span>
                {isCurrent && <em>当前</em>}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </>
  );
}
